import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SmartLink from '../common/SmartLink'
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../lib/supabaseClient';
import { useRegisterEditor } from '../lib/EditorContext';
import { defaultContent } from '../lib/defaultContent';

const { FiHeart, FiStar, FiSun, FiPlayCircle } = FiIcons;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const defaultAffirmations = [
  {
    id: 1,
    title: "Change Negative Self-Talk",
    iconName: "FiSun",
    desc: "Replace 'I'm messing this up' with 'I am learning alongside my child'. Your internal dialogue sets the tone for your external reactions.",
    iconBg: "bg-blue-100",
    iconBorder: "border-blue-200",
    iconColor: "text-blue-600"
  },
  {
    id: 2,
    title: "Celebrate Wins",
    iconName: "FiStar",
    desc: "Did you stay calm during a meltdown? Did you repair after yelling? Acknowledge the small victories—they build the foundation for lasting change.",
    iconBg: "bg-yellow-100",
    iconBorder: "border-yellow-200",
    iconColor: "text-yellow-600"
  },
  {
    id: 3,
    title: "Daily Affirmations",
    iconName: "FiHeart",
    desc: "Start your day rooted in truth. 'Challenging behavior is not a parenting failure.' Anchor yourself before the chaos begins.",
    iconBg: "bg-red-100",
    iconBorder: "border-red-200",
    iconColor: "text-brand-rose"
  }
];

const affirmationExamples = [
  "I can stay calm even when things feel out of control.",
  "My child's behavior is communication, not an attack.",
  "I don't have to solve everything right now.",
  "This moment will pass.",
  "I am doing the best I can with what I have.",
  "Connection matters more than control.",
  "Small wins count.",
  "I will get through this phase."
];

export default function Affirmations() {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from('page_content')
          .select('content')
          .eq('page_id', 'affirmations')
          .single();

        if (error) {
          console.warn('Could not fetch affirmations content, using defaults.', error.message);
        } else if (data && data.content) {
          setContent(data.content);
        }
      } catch (err) {
        console.error('Error fetching affirmations content:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContent();
  }, []);

  const title = content?.title || "Affirmations";
  const subtitle = content?.subtitle || "Parenting is hard enough without your inner critic joining in. It's time to speak to yourself with the same compassion you want to offer your child.";
  const overline = content?.overline || "Nurture Your Inner Voice";

  const currentAffirmations = content?.affirmations || defaultAffirmations;
  const currentExamples = content?.affirmationExamples || affirmationExamples;

  const wantToLearnMore = content?.wantToLearnMore || defaultContent.affirmations.wantToLearnMore;

  const handleSaveSuccess = (newContent) => {
    setContent(newContent);
  };

  useRegisterEditor(
    'affirmations',
    { title, subtitle, overline, affirmations: currentAffirmations, affirmationExamples: currentExamples, wantToLearnMore },
    handleSaveSuccess,
    'Affirmations Page'
  );

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" exit="hidden" className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#333] dark:text-[#fff] font-bold tracking-widest uppercase text-xs mb-3 block">{overline}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-[#333] dark:text-[#fff] mb-6">{title}</h1>
          <p className="text-lg text-[#333] dark:text-[#fff]">
            {subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {currentAffirmations.map((item) => (
            <motion.div key={item.id} variants={fadeUp} className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100 flex flex-col items-center text-center hover:shadow-lg transition-all duration-300">
              <div className={`w-16 h-16 ${item.iconBg} dark:bg-brand-slate rounded-full flex items-center justify-center mb-6 shadow-sm border ${item.iconBorder}`}>
                <SafeIcon icon={FiIcons[item.iconName] || FiIcons.FiHeart} className={`${item.iconColor} w-8 h-8`} />
              </div>
              <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-4">{item.title}</h3>
              <p className="text-[#333] dark:text-[#fff] leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div variants={fadeUp} className="mb-20 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-[#333] dark:text-[#fff] mb-8 text-center">Daily Affirmation Practice</h2>
          <div className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100">
            <div className="space-y-4">
              {currentExamples.map((example, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="mt-1 text-brand-rose">
                    <SafeIcon icon={FiHeart} />
                  </div>
                  <p className="text-[#333] dark:text-[#fff] text-lg font-medium">{example}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="bg-brand-rose text-white rounded-[3rem] p-10 md:p-16 text-center max-w-4xl mx-auto shadow-xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2"></div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">{wantToLearnMore.title}</h2>
            <p className="text-lg mb-10 max-w-2xl mx-auto opacity-90">
              {wantToLearnMore.description}
            </p>
            <SmartLink to={wantToLearnMore.buttonLink} className="inline-flex items-center gap-2 px-10 py-4 bg-white text-brand-rose rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-lg">
              <SafeIcon icon={FiPlayCircle} /> {wantToLearnMore.buttonText}
            </SmartLink>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}